import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useOutletContext } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { money } from "@/lib/money";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, PencilSimple, Trash } from "@phosphor-icons/react";
import NewProposalModal from "@/components/NewProposalModal";
import { DraftBanner, DraftStatusPill, REVIEWABLE } from "./shared";

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[150px_1fr] gap-3 py-2 border-b border-slate-100 last:border-0 text-sm">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 pt-0.5">{label}</div>
      <div className="text-slate-800">{value ?? "—"}</div>
    </div>
  );
}

export default function SalesProposalDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { autoSend } = useOutletContext();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [editing, setEditing] = useState(false);
  const load = () => api.get(`/sales/proposals/${id}`).then((r) => (r.data ? setP(r.data) : setMissing(true))).catch(() => setMissing(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <div className="text-slate-500">This proposal isn't one of yours (or was removed). <Link to="/sales/proposals" className="underline">Back to proposals</Link></div>;
  if (!p) return <div className="text-slate-500">Loading…</div>;
  const reviewable = REVIEWABLE.includes(p.status);

  const withdraw = async () => {
    if (!window.confirm(`Withdraw the proposal for "${p.company_name}"?`)) return;
    try { await api.delete(`/sales/proposals/${id}`); toast.success("Proposal withdrawn."); nav("/sales/proposals"); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  return (
    <div data-testid="sales-proposal-detail">
      <Link to="/sales/proposals" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="sales-proposal-back"><ArrowLeft size={14} weight="bold" /> All proposals</Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="tinylabel text-emerald-700">Proposal</div>
          <h1 className="text-3xl font-black">{p.company_name}</h1>
          <div className="mt-2 flex items-center gap-2"><DraftStatusPill doc={p} /><span className="text-xs text-slate-500">{p.location}</span></div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`${API_BASE}/sales/proposals/${id}/pdf`} target="_blank" rel="noreferrer" data-testid="sales-proposal-pdf-btn"
             className="h-10 px-3 border border-slate-300 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:bg-slate-50 bg-white"><FilePdf size={16} weight="bold" /> Preview PDF</a>
          {reviewable && (
            <>
              <button onClick={() => setEditing(true)} data-testid="sales-proposal-edit-btn" className="h-10 px-3 bg-teal-700 hover:bg-teal-800 text-white rounded-md font-semibold text-sm inline-flex items-center gap-2"><PencilSimple size={16} weight="bold" /> Edit &amp; resubmit</button>
              <button onClick={withdraw} data-testid="sales-proposal-withdraw-btn" className="h-10 px-3 border border-slate-300 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 bg-white"><Trash size={16} weight="bold" /> Withdraw</button>
            </>
          )}
        </div>
      </div>

      <div className="mb-4"><DraftBanner doc={p} kind="proposal" onEdit={() => setEditing(true)} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <div className="tinylabel mb-2">Client</div>
          <Row label="Contact" value={p.contact_name} />
          <Row label="Email" value={p.contact_email} />
          <Row label="Phone" value={p.contact_phone} />
          {p.cc_emails?.length > 0 && <Row label="Copies to" value={p.cc_emails.join(", ")} />}
          <Row label="Location" value={p.location} />
          <Row label="Scope" value={<span className="whitespace-pre-line">{p.description}</span>} />
          <Row label="Start · duration" value={`${p.anticipated_start_date || "TBD"} · ${p.anticipated_duration || "TBD"}`} />
        </section>
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <div className="tinylabel mb-2">Commercial terms</div>
          <Row label="Bill rate" value={<span className="font-mono">{money(p.hourly_rate)} / hr</span>} />
          {p.minimum_hours_applies && <Row label="Minimum hours" value={`${p.minimum_hours_per_week} hrs / week`} />}
          {p.per_diem_applies && <Row label="Per diem" value={<span className="font-mono">{money(p.per_diem_rate)} / day · {String(p.per_diem_frequency || "").replace(/_/g, " ")}</span>} />}
          {p.ot_rate_applies && <Row label="Overtime" value={<span className="font-mono">{money(p.ot_rate)} / hr after {p.ot_threshold_hours || 40} hrs</span>} />}
          {p.holiday_rate_applies && <Row label="Holiday rate" value={<span className="font-mono">{money(p.holiday_rate)} / hr</span>} />}
          {p.mobilization_applies && <Row label="Mobilization" value={p.mobilization_hours_cap ? `Paid, capped at ${p.mobilization_hours_cap} hrs each way` : "Paid"} />}
          {p.special_terms && <Row label="Special terms" value={<span className="whitespace-pre-line">{p.special_terms}</span>} />}
          <Row label="Link validity" value={`${p.validity_days || 30} days`} />
          <Row label="Submitted" value={p.submitted_at ? String(p.submitted_at).slice(0, 10) : "—"} />
          {p.reviewed_at && <Row label="Reviewed" value={`${String(p.reviewed_at).slice(0, 10)}${p.reviewed_by ? ` by ${p.reviewed_by}` : ""}`} />}
        </section>
      </div>

      {editing && <NewProposalModal mode="sales" autoSend={autoSend} initial={p} onClose={() => setEditing(false)} onCreated={load} />}
    </div>
  );
}
